import type { Contact } from '@efdigital/ll-addon-data';
import { queryContacts } from '@efdigital/ll-admin-v2/data/builders/content/contacts';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { Store } from '@warp-drive/core';
import { Request } from '@warp-drive/ember';
import BsModal from 'ember-bootstrap/components/bs-modal';

import { getPaginationTotal } from '../../helpers/get-pagination-total.ts';
import { PaginationByPageNumber } from '../pagination.gts';

function formatContactName(contact: Contact): string {
  return [contact.title, contact.firstname, contact.lastname, contact.suffix]
    .filter(Boolean)
    .join(' ');
}

type Signature = {
  Args: {
    /**
     * Callback when a contact is selected
     */
    onSelect: (contact: Contact) => void;
  };
  Blocks: {
    input: [showModal: () => void];
  };
};

export class ContactPicker extends Component<Signature> {
  @tracked isOpen = false;
  @tracked searchState = {
    firstname: '',
    lastname: '',
    organisation: '',
    pageIndex: 0,
  };
  @service declare store: Store;

  readonly pageSize = 30;

  get query() {
    const offset = this.searchState.pageIndex * this.pageSize;
    return queryContacts(
      this.searchState.firstname,
      this.searchState.lastname,
      this.searchState.organisation,
      offset,
      this.pageSize
    );
  }

  showModal = () => {
    this.isOpen = true;
    this.searchState = {
      firstname: '',
      lastname: '',
      organisation: '',
      pageIndex: 0,
    };
  };

  modalClosed = () => {
    this.isOpen = false;
  };

  updateFirstnameSearch = (event: Event) => {
    this.searchState = {
      ...this.searchState,
      firstname: (event.target as HTMLInputElement).value,
      pageIndex: 0,
    };
  };

  updateLastnameSearch = (event: Event) => {
    this.searchState = {
      ...this.searchState,
      lastname: (event.target as HTMLInputElement).value,
      pageIndex: 0,
    };
  };

  updateOrganisationSearch = (event: Event) => {
    this.searchState = {
      ...this.searchState,
      organisation: (event.target as HTMLInputElement).value,
      pageIndex: 0,
    };
  };

  selectContact = (contact: Contact) => {
    this.args.onSelect(contact);
    this.isOpen = false;
  };

  setPageIndex = (pageIndex: number): void => {
    this.searchState = { ...this.searchState, pageIndex };
  };

  <template>
    {{yield this.showModal to="input"}}
    <BsModal
      @open={{this.isOpen}}
      @onHidden={{this.modalClosed}}
      @size="xl"
      @renderInPlace={{true}}
      @backdropClose={{false}}
      as |modal|
    >
      <modal.header>
        <h2 class="gud-eyebrow mb-2">Select Contact</h2>
      </modal.header>
      <modal.body>
        <div class="row g-3 mb-4">
          <div class="col-md-4">
            <label for="firstnameSearch" class="form-label">First Name</label>
            <input
              type="text"
              id="firstnameSearch"
              class="form-control"
              placeholder="First name..."
              value={{this.searchState.firstname}}
              {{on "input" this.updateFirstnameSearch}}
            />
          </div>
          <div class="col-md-4">
            <label for="lastnameSearch" class="form-label">Last Name</label>
            <input
              type="text"
              id="lastnameSearch"
              class="form-control"
              placeholder="Last name..."
              value={{this.searchState.lastname}}
              {{on "input" this.updateLastnameSearch}}
            />
          </div>
          <div class="col-md-4">
            <label
              for="organisationSearch"
              class="form-label"
            >Organisation</label>
            <input
              type="text"
              id="organisationSearch"
              class="form-control"
              placeholder="Organisation..."
              value={{this.searchState.organisation}}
              {{on "input" this.updateOrganisationSearch}}
            />
          </div>
        </div>

        <Request @query={{this.query}}>
          <:content as |contacts|>
            {{#if contacts.data.length}}
              {{#if contacts.meta.page}}
                <PaginationByPageNumber
                  @pageIndex={{this.searchState.pageIndex}}
                  @pageSize={{this.pageSize}}
                  @total={{getPaginationTotal contacts}}
                  @onPageIndexChange={{this.setPageIndex}}
                  class="d-flex flex-row justify-content-center my-4"
                />
              {{/if}}

              <div class="table-responsive">
                <table
                  class="table table-bordered table-striped table-sm align-middle"
                >
                  <thead>
                    <tr>
                      <th scope="col">Name</th>
                      <th scope="col">Email</th>
                      <th scope="col">Organization</th>
                      <th scope="col" class="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {{#each contacts.data as |contact|}}
                      <tr>
                        <td>
                          <strong>{{formatContactName contact}}</strong>
                        </td>
                        <td class="text-muted">{{contact.email}}</td>
                        <td class="text-muted">{{contact.organisation}}</td>
                        <td class="text-center">
                          <button
                            type="button"
                            class="btn btn-sm btn-primary"
                            {{on "click" (fn this.selectContact contact)}}
                          >
                            Select
                          </button>
                        </td>
                      </tr>
                    {{/each}}
                  </tbody>
                </table>
              </div>

              {{#if contacts.meta.page}}
                <PaginationByPageNumber
                  @pageIndex={{this.searchState.pageIndex}}
                  @pageSize={{this.pageSize}}
                  @total={{getPaginationTotal contacts}}
                  @onPageIndexChange={{this.setPageIndex}}
                  class="d-flex flex-row justify-content-center my-4"
                />
              {{/if}}
            {{else}}
              <div
                class="alert alert-info d-flex align-items-center"
                role="alert"
              >
                <span class="bi bi-info-circle me-2"></span>
                <div>No contacts found.</div>
              </div>
            {{/if}}
          </:content>
          <:loading>
            <div class="text-center py-5">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading contacts...</span>
              </div>
              <p class="mt-2 text-muted">Loading contacts...</p>
            </div>
          </:loading>
          <:error as |error|>
            <div
              class="alert alert-danger d-flex align-items-center"
              role="alert"
            >
              <span class="bi bi-exclamation-triangle me-2"></span>
              <div>Error: {{error.message}}</div>
            </div>
          </:error>
        </Request>
      </modal.body>
      <modal.footer class="text-center">
        <button
          type="button"
          class="btn btn-outline-secondary"
          {{on "click" this.modalClosed}}
        >
          Cancel
        </button>
      </modal.footer>
    </BsModal>
  </template>
}

export default ContactPicker;
